import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Users, AlertTriangle, CalendarClock, TrendingUp } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { SetupChecklist, type SetupStep } from '@/components/SetupChecklist'
import { useAuth } from '@/auth/useAuth'
import { useDashboardTiles, useEmployees, usePayComponents, usePaySchedules, usePayRuns } from '@/features/payroll/queries'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'

function Tile({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: typeof Users
  label: string
  value: string | number
  hint?: string
  accent?: 'default' | 'warning' | 'success'
}) {
  const accentClass =
    accent === 'warning' ? 'text-amber-500' :
    accent === 'success' ? 'text-emerald-500' :
    'text-primary'
  return (
    <Card>
      <CardContent className="flex items-start gap-4 p-5">
        <div className={`mt-1 ${accentClass}`}>
          <Icon className="size-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
          {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
        </div>
      </CardContent>
    </Card>
  )
}

export function PayrollDashboardPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const { data, isLoading, isError, error, refetch } = useDashboardTiles()
  const tiles = data?.data
  const canCreate = hasPermission('payroll.runs.create')

  const employees = useEmployees()
  const components = usePayComponents()
  const schedules = usePaySchedules()
  const runs = usePayRuns()

  const steps = useMemo<SetupStep[]>(
    () => [
      {
        title: 'Add your employees',
        description: 'Create employee records with compensation and tax details.',
        to: '/payroll/people',
        cta: 'Add employees',
        done: (employees.data?.data?.length ?? 0) > 0,
      },
      {
        title: 'Set up pay components',
        description: 'Seed the standard US earnings, taxes, and deductions.',
        to: '/payroll/compensation',
        cta: 'Set up components',
        done: (components.data?.data?.length ?? 0) > 0,
      },
      {
        title: 'Create a pay schedule',
        description: 'Choose how often you pay (weekly, biweekly, monthly).',
        to: '/payroll/setup',
        cta: 'Add schedule',
        done: (schedules.data?.data?.length ?? 0) > 0,
      },
      {
        title: 'Run your first payroll',
        description: 'Calculate, approve, post, and pay your team.',
        to: '/payroll/runs',
        cta: 'Run payroll',
        done: (runs.data?.data?.length ?? 0) > 0,
      },
    ],
    [employees.data, components.data, schedules.data, runs.data],
  )

  return (
    <Page
      title="Payroll"
      description="Run payroll, pay employees and contractors, file payroll taxes, and reconcile to the ledger."
      actions={
        canCreate ? (
          <Button onClick={() => navigate('/payroll/runs')}>
            <Plus className="mr-2 size-4" /> New pay run
          </Button>
        ) : undefined
      }
    >
      <SetupChecklist title="Get started with Payroll" steps={steps} />
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : isError || !tiles ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="text-base font-semibold text-destructive">Couldn&apos;t load Payroll tiles</h2>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'The dashboard endpoint did not respond. The BFF may have been restarting - try again in a moment.'}
            </p>
            <div>
              <Button variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              icon={Users}
              label="Active employees"
              value={tiles.active_employees}
              hint="W-2 + contractors on the books"
            />
            <Tile
              icon={CalendarClock}
              label="Pending runs"
              value={tiles.pending_runs}
              accent={tiles.pending_runs > 0 ? 'warning' : 'default'}
              hint="Draft, review, or awaiting approval"
            />
            <Tile
              icon={TrendingUp}
              label="YTD gross payroll"
              value={formatPayrollMoney(tiles.ytd_gross_minor_units || '0', 'USD')}
              hint="Posted + paid runs this calendar year"
            />
            <Tile
              icon={AlertTriangle}
              label="Open anomalies"
              value={tiles.open_anomalies}
              accent={tiles.open_anomalies > 0 ? 'warning' : 'success'}
              hint="AI-flagged in the last 30 days"
            />
          </div>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="text-base font-semibold">Next pay run</h2>
              {tiles.next_run ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">{tiles.next_run.run_number}</span>
                      <Badge variant="outline">{tiles.next_run.status}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Pay date {formatDate(tiles.next_run.pay_date)}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => navigate('/payroll/runs')}>
                    View pay runs
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    No upcoming pay runs. Configure a pay schedule under Settings, then create your first
                    run.
                  </p>
                  {canCreate ? (
                    <div className="flex gap-2">
                      <Button onClick={() => navigate('/payroll/runs')}>
                        <Plus className="mr-2 size-4" /> Create pay run
                      </Button>
                      <Button variant="outline" onClick={() => navigate('/payroll/setup')}>
                        Settings
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Page>
  )
}
